/* Product detail magnifier: keep the original studio treatment and gallery intact. */
(() => {
  'use strict';
  const magnification = 3;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const finePointer = window.matchMedia('(hover:hover) and (pointer:fine)');

  document.querySelectorAll('.dialog-gallery__media, .pdp__media').forEach((media, index) => {
    const image = media.querySelector('#dialog-image, [data-pdp-main]');
    if (!image || media.querySelector('.st-product-zoom-toggle')) return;
    const dialog = media.closest('dialog');
    let layer = null, gesture = null, lastTap = null, lastTouch = -Infinity;
    let x = 0, y = 0, width = 0, height = 0, frame = 0, detailLoading = false;
    const isZoomed = () => Boolean(layer);
    const controls = (target) => target.closest('button, a, input, select');

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'st-product-zoom-toggle';
    toggle.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5M7.5 10.5h6"/><path class="st-product-zoom-plus" d="M10.5 7.5v6"/></svg>';
    const hint = document.createElement('span');
    hint.className = 'st-product-zoom-hint';
    hint.id = 'st-product-zoom-hint-' + index;
    hint.setAttribute('aria-live', 'polite');
    toggle.setAttribute('aria-describedby', hint.id);
    media.append(toggle, hint);
    if (!media.hasAttribute('tabindex')) media.tabIndex = 0;
    media.setAttribute('role', 'group');
    media.setAttribute('aria-label', 'Product image gallery');

    const updateControls = () => {
      const zoomed = isZoomed();
      toggle.setAttribute('aria-pressed', String(zoomed));
      toggle.setAttribute('aria-label', zoomed ? 'Reset image zoom' : 'Zoom in on product image');
      toggle.title = zoomed ? 'Reset zoom' : 'Zoom in';
      toggle.disabled = !image.complete || !image.naturalWidth;
      toggle.setAttribute('aria-busy', String(detailLoading));
      hint.textContent = zoomed
        ? (detailLoading ? 'Loading full-size detail…' : finePointer.matches ? 'Drag to explore · Double-click to reset' : 'Drag to explore · Double-tap to reset')
        : (finePointer.matches ? 'Double-click to zoom' : 'Double-tap to zoom');
    };
    const announce = () => media.dispatchEvent(new CustomEvent('st:product-zoom-change', {
      bubbles: true, detail: { zoomed: isZoomed() }
    }));
    const releaseGesture = () => {
      const previous = gesture;
      gesture = null;
      if (previous && media.hasPointerCapture(previous.id)) media.releasePointerCapture(previous.id);
      media.classList.remove('is-product-zoom-dragging');
    };
    const paint = () => {
      frame = 0;
      if (layer) layer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${magnification})`;
    };
    const pan = (nextX, nextY) => {
      x = clamp(nextX, width * (1 - magnification), 0);
      y = clamp(nextY, height * (1 - magnification), 0);
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const reset = () => {
      if (!layer) { lastTap = null; return; }
      cancelAnimationFrame(frame); frame = 0;
      releaseGesture(); lastTap = null;
      layer.remove(); layer = null; detailLoading = false;
      media.classList.remove('is-product-zoomed');
      updateControls(); announce();
    };
    const zoom = (clientX, clientY) => {
      if (isZoomed()) { reset(); return; }
      if (!image.complete || !image.naturalWidth || (dialog && !dialog.open)) return;
      // Copy the rendered geometry and optical treatment, including per-image silver filters.
      // Keeping the original image in place also preserves layout and gallery selectors.
      image.classList.remove('is-in-next', 'is-in-prev');
      const bounds = media.getBoundingClientRect();
      const photo = image.getBoundingClientRect();
      const treatment = getComputedStyle(image);
      width = media.clientWidth; height = media.clientHeight;
      if (!width || !height || !photo.width || !photo.height) return;
      const left = bounds.left + media.clientLeft, top = bounds.top + media.clientTop;
      layer = document.createElement('div');
      layer.className = 'st-product-zoom-layer';
      layer.setAttribute('aria-hidden', 'true');
      const detail = document.createElement('img');
      detail.className = 'st-product-zoom-image';
      detail.alt = ''; detail.draggable = false;
      detail.src = image.currentSrc || image.src;
      Object.assign(detail.style, {
        width: photo.width + 'px', height: photo.height + 'px',
        left: (photo.left - left) + 'px', top: (photo.top - top) + 'px',
        objectFit: treatment.objectFit, objectPosition: treatment.objectPosition,
        filter: treatment.filter, mixBlendMode: treatment.mixBlendMode,
        backgroundColor: treatment.backgroundColor
      });
      layer.append(detail); media.append(layer);
      // Fetch more detail only on zoom, preserving the storefront's smaller initial images.
      try {
        const highResolution = new URL(detail.src, document.baseURI);
        if (highResolution.pathname.startsWith('/cdn/shop/') || (highResolution.hostname === 'cdn.shopify.com' && highResolution.pathname.startsWith('/s/files/'))) {
          // Use the complete source rather than magnifying a CDN thumbnail.
          highResolution.searchParams.delete('width');
          highResolution.searchParams.delete('height');
          if (highResolution.href !== detail.src) {
            const probe = new Image();
            probe.decoding = 'async';
            detailLoading = true;
            const finishDetail = () => {
              if (!detail.isConnected || detail.parentElement !== layer) return;
              detailLoading = false;
              updateControls();
            };
            probe.onload = () => {
              if (detail.isConnected && detail.parentElement === layer) detail.src = highResolution.href;
              finishDetail();
            };
            probe.onerror = finishDetail;
            probe.src = highResolution.href;
          }
        }
      } catch (_) { /* The already loaded source remains available if a URL cannot be resized. */ }
      x = clamp(width / 2 - clamp(clientX - left, 0, width) * magnification, width * (1 - magnification), 0);
      y = clamp(height / 2 - clamp(clientY - top, 0, height) * magnification, height * (1 - magnification), 0);
      paint();
      media.classList.add('is-product-zoomed');
      lastTap = null; updateControls(); announce();
    };

    toggle.addEventListener('click', () => {
      const bounds = media.getBoundingClientRect();
      zoom(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    });
    media.addEventListener('dblclick', (event) => {
      if (event.button !== 0 || controls(event.target) || performance.now() - lastTouch < 750) return;
      event.preventDefault();
      zoom(event.clientX, event.clientY);
    });
    media.addEventListener('dragstart', (event) => {
      if (event.target === image) event.preventDefault();
    });
    media.addEventListener('pointerdown', (event) => {
      if (controls(event.target) || event.button !== 0) return;
      if (!event.isPrimary) { releaseGesture(); lastTap = null; return; }
      gesture = { id: event.pointerId, type: event.pointerType, startX: event.clientX, startY: event.clientY,
        time: performance.now(), x, y, moved: false, zoomed: isZoomed() };
      if (isZoomed()) {
        media.setPointerCapture(event.pointerId);
        media.classList.add('is-product-zoom-dragging');
        event.preventDefault();
      }
    });
    media.addEventListener('pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.startX, dy = event.clientY - gesture.startY;
      if (Math.hypot(dx, dy) > 12) { gesture.moved = true; lastTap = null; }
      if (gesture.zoomed && isZoomed()) {
        pan(gesture.x + dx, gesture.y + dy);
        event.preventDefault();
      }
    });
    media.addEventListener('pointerup', (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const tap = gesture, now = performance.now();
      releaseGesture();
      if (tap.type === 'mouse') return;
      lastTouch = now;
      const distance = Math.hypot(event.clientX - tap.startX, event.clientY - tap.startY);
      if (tap.moved || distance > 12 || now - tap.time > 300) { lastTap = null; return; }
      if (lastTap && now - lastTap.time <= 400 && Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) <= 30) {
        event.preventDefault();
        zoom(event.clientX, event.clientY);
        lastTap = null;
      } else {
        lastTap = { time: now, x: event.clientX, y: event.clientY };
      }
    });
    const cancelGesture = () => { releaseGesture(); lastTap = null; };
    media.addEventListener('pointercancel', cancelGesture);
    media.addEventListener('lostpointercapture', (event) => {
      if (gesture && gesture.id === event.pointerId) cancelGesture();
    });
    media.addEventListener('keydown', (event) => {
      if (!isZoomed() || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); reset(); return; }
      if (event.target.closest('.st-gallery-arrow')) return;
      const movement = { ArrowLeft: [60, 0], ArrowRight: [-60, 0], ArrowUp: [0, 60], ArrowDown: [0, -60] }[event.key];
      if (!movement) return;
      event.preventDefault(); event.stopPropagation();
      pan(x + movement[0], y + movement[1]);
    }, true);
    if (dialog) {
      dialog.addEventListener('cancel', (event) => {
        if (!isZoomed()) return;
        event.preventDefault(); event.stopImmediatePropagation(); reset();
      }, true);
      dialog.addEventListener('close', reset);
    }
    media.addEventListener('st:product-zoom-reset', reset);
    image.addEventListener('load', () => { reset(); updateControls(); });
    image.addEventListener('error', () => { reset(); updateControls(); });
    image.addEventListener('st:pdp-media-swapped', reset);
    new MutationObserver((records) => {
      if (records.some((record) => ['src', 'srcset'].includes(record.attributeName))) { reset(); updateControls(); }
    }).observe(image, { attributes: true, attributeFilter: ['src', 'srcset'] });
    let previousWidth = media.clientWidth, previousHeight = media.clientHeight;
    if (window.ResizeObserver) new ResizeObserver(() => {
      if (media.clientWidth !== previousWidth || media.clientHeight !== previousHeight) reset();
      previousWidth = media.clientWidth; previousHeight = media.clientHeight;
    }).observe(media);
    window.addEventListener('pagehide', reset);
    finePointer.addEventListener('change', updateControls);
    updateControls();
  });
})();
